import { useRecoilValue } from "recoil"
import { currentPatientFileInfoAtom } from "../../../Atoms/PatientFile"
import { useModalitiesProvider } from "../../../../../../../Providers/ModalitiesProvider"
import { useEnvironmentVariablesProvider } from "../../../../../../../Providers/EnvironmentVariablesProvider"
import { DataSource } from "../../../Types/DataSource"
import { analyticTracesAtom } from "../../../Atoms/Analytics"
import { getSupportedRenderStrategies, RenderStrategy, TraceOption } from "../../../Types/Trace"
import { Analytic } from "../../../Types/AnalysisDetails"

type useTraceOptionsProps = {
    includeAnalytics: boolean
}

export type { TraceConfigSeed, TraceOption } from "../../../Types/Trace"

const CPPOPT_TRACE_NAMES = new Set([
    "CPPOpt", "CPPOpt_LLA", "CPPOpt_ULA",
    "CPPOpt (multi-window)", "CPPOpt_LLA (multi-window)", "CPPOpt_ULA (multi-window)"
])

const MAPOPT_TRACE_NAMES = new Set([
    "MAPOpt", "MAPOpt_LLA", "MAPOpt_ULA",
    "MAPOpt (multi-window)", "MAPOpt_LLA (multi-window)", "MAPOpt_ULA (multi-window)"
])

const PRX_TRACE_NAMES = new Set([
    "PRx"
])

const COX_TRACE_NAMES = new Set([
    "COx"
])

export const useTraceOptions = (options?: useTraceOptionsProps ): TraceOption[] => {
    const { patientModalities } = useRecoilValue(currentPatientFileInfoAtom)
    const modalitiesProvider = useModalitiesProvider()
    const { environmentVariables } = useEnvironmentVariablesProvider()
    const analyticTraceOptions = useRecoilValue(analyticTracesAtom)

    const traceOptions = Object.entries(modalitiesProvider.modalities)
        .filter(([modality]) => !modality.startsWith('EEG'))
        .map(([name, modalityObj]): TraceOption => {
            let source = ''

            if (modalityObj && modalityObj.file_name) {
                const parts = modalityObj.file_name.split(',');
                const dataIdx = parts.lastIndexOf('data');

                if (dataIdx > 0) {
                    source = parts[dataIdx - 1];
                }
            }

            return {
                label: name,
                source,
                value: {
                    name,
                    dataKey: name,
                    rawDataKey: name,
                    dataSource: DataSource.CURRENT_PATIENT,
                    units: "",
                    isCompositePart: false,
                },
                supportedRenderStrategies: getSupportedRenderStrategies(name),
                highlight: patientModalities.includes(name)
            }
        })

    if (options?.includeAnalytics) {
        const filteredAnalytics = analyticTraceOptions.filter(trace => {
            if (!environmentVariables.FEATURE_FLAG_CPPOPT && CPPOPT_TRACE_NAMES.has(trace.name)) return false
            if (!environmentVariables.FEATURE_FLAG_MAPOPT && MAPOPT_TRACE_NAMES.has(trace.name)) return false
            if (!environmentVariables.FEATURE_FLAG_PRX && PRX_TRACE_NAMES.has(trace.name)) return false
            if (!environmentVariables.FEATURE_FLAG_COX && COX_TRACE_NAMES.has(trace.name)) return false
            return true
        })

        traceOptions.push(...filteredAnalytics.map(traceDataConfig => ({
            label: traceDataConfig.name,
            value: traceDataConfig,
            supportedRenderStrategies: traceDataConfig.onDemandAnalysis?.analytic === Analytic.QEEG_DSA
                ? [RenderStrategy.SPECTROGRAM]
                : [RenderStrategy.LINE, RenderStrategy.HEATMAP],
            highlight: false,
            source: ""
        })))
    }

    traceOptions.sort((a, b) => {
        const aIsAvailable = a.highlight
        const bIsAvailable = b.highlight

        if (aIsAvailable && !bIsAvailable) {
            return -1 // a comes first
        }
        if (!aIsAvailable && bIsAvailable) {
            return 1 // b comes first
        }

        // If both are available or both are not available, sort alphabetically by label
        return a.label.localeCompare(b.label)
    })

    return traceOptions
}
